import { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import SearchSelect from "../../components/SearchSelect";
import {
  useActivityTypes,
  useApplicationSender,
  useDeviceModels,
  useDeviceSerialChecker,
  useDistrict,
  useDraftRemover,
  useDraftSaver,
  useObjectTypes,
  useRegion,
  useStreet
} from "../../api/DataAPI";
import useStrings from "../../api/StringsAPI";

interface DocumentRecord {
  id?: number;
  taxNumber?: string;
  orgName?: string;
  deviceSerial?: string;
  sealNumber?: string;
  samModule?: string;
  simNumber?: string;
  fiscalNumber?: string;
  phone?: string;
  email?: string;
  addressAdditional?: string;
  status?: { id: string; name: string };
  statusText?: string;
  region?: { id: number };
  district?: { id: number };
  street?: { id: number };
  activityType?: { id: number };
  objectType?: { id: number };
  deviceModel?: { id: number };
}

interface Props {
  visible: boolean;
  onHide: () => void;
  document: DocumentRecord | null;
  onSuccess: () => void;
}

// The device identifiers are validated against each other, not on their own.
const PAIRED_FIELDS = ['samModule', 'fiscalNumber', 'deviceSerial', 'sealNumber'];

export default function NewApplicationDialog({ visible, onHide, document, onSuccess }: Props) {
  const str = useStrings();
  const isEdit = document !== null && document.id !== undefined;
  const isDraft = document?.status?.id === 'DRAFT';
  const isError = document?.status?.id === 'ERROR' || document?.status?.id === 'EXCEPTION';

  const sendMutation = useApplicationSender();
  const saveDraftMutation = useDraftSaver();
  const deleteDraftMutation = useDraftRemover();
  const checkSerialMutation = useDeviceSerialChecker();

  const regionQuery = useRegion();
  const objectTypesQuery = useObjectTypes();
  const deviceModelsQuery = useDeviceModels();
  const activityTypesQuery = useActivityTypes();

  const [form, setForm] = useState({
    taxNumber: '',
    orgName: '',
    deviceSerial: '',
    sealNumber: '',
    samModule: '',
    simNumber: '',
    fiscalNumber: '',
    phone: '',
    email: '',
    addressAdditional: '',
    region: '' as string | number,
    district: '' as string | number,
    street: '' as string | number,
    activityType: '' as string | number,
    objectType: '' as string | number,
    deviceModel: '' as string | number,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const districtQuery = useDistrict(form.region as number);
  const streetQuery = useStreet(form.district as string);

  useEffect(() => {
    if (document) {
      setForm({
        taxNumber: document.taxNumber || '',
        orgName: document.orgName || '',
        deviceSerial: document.deviceSerial || '',
        sealNumber: document.sealNumber || '',
        samModule: document.samModule || '',
        simNumber: document.simNumber || '',
        fiscalNumber: document.fiscalNumber || '',
        phone: document.phone || '',
        email: document.email || '',
        addressAdditional: document.addressAdditional || '',
        region: document.region?.id || '',
        district: document.district?.id || '',
        street: document.street?.id || '',
        activityType: document.activityType?.id || '',
        objectType: document.objectType?.id || '',
        deviceModel: document.deviceModel?.id || '',
      });
    } else {
      setForm({
        taxNumber: '', orgName: '', deviceSerial: '', sealNumber: '', samModule: '',
        simNumber: '', fiscalNumber: '', phone: '', email: '', addressAdditional: '',
        region: '', district: '', street: '', activityType: '', objectType: '', deviceModel: '',
      });
    }
    setErrors({});
    setMessage(null);
  }, [document, visible]);

  const updateForm = (changes: Partial<typeof form>) => {
    setForm({ ...form, ...changes });

    // The edited field's own error goes - and so does a cross-field error parked on
    // another identifier, because correcting the SAM module resolves the mismatch
    // that is reported under the SAM number rather than under the module.
    const pairMessages = [str('mustEqualSam'), str('mustDifferSamSerial'), str('mustDifferSeal')];
    const touchesPair = Object.keys(changes).some(key => PAIRED_FIELDS.includes(key));
    const stale = Object.keys(errors).filter(key => key in changes
      || (touchesPair && PAIRED_FIELDS.includes(key) && pairMessages.includes(errors[key])));
    if (stale.length) {
      const nextErrors = { ...errors };
      stale.forEach(key => delete nextErrors[key]);
      setErrors(nextErrors);
    }
  };

  // Submit checks all required fields; a draft only needs the tax number.
  // Format rules apply to every filled-in field in both cases.
  const validate = (requireAll: boolean): boolean => {
    const newErrors: Record<string, string> = {};
    const required = !requireAll ? ['taxNumber'] : ['taxNumber', 'orgName', 'deviceSerial', 'sealNumber', 'samModule', 'fiscalNumber', 'region', 'district', 'street', 'addressAdditional', 'phone', 'activityType', 'objectType', 'deviceModel'];
    required.forEach(field => {
      if (!form[field as keyof typeof form]) newErrors[field] = str('requiredField');
    });

    const eightDigits = /^[0-9]{8}$/;
    const { samModule, fiscalNumber, deviceSerial, sealNumber } = form;
    if (samModule && !eightDigits.test(samModule)) newErrors.samModule = str('mustBeEightDigits');
    if (fiscalNumber && !eightDigits.test(fiscalNumber)) newErrors.fiscalNumber = str('mustBeEightDigits');
    if (deviceSerial && !eightDigits.test(deviceSerial)) newErrors.deviceSerial = str('mustBeEightDigits');
    if (!newErrors.fiscalNumber && fiscalNumber && samModule && fiscalNumber !== samModule) {
      newErrors.fiscalNumber = str('mustEqualSam');
    }
    if (sealNumber && (!/^\S{8,255}$/.test(sealNumber)
        || (sealNumber.match(/\p{L}/gu) || []).length < 3
        || !/[0-9]/.test(sealNumber))) {
      newErrors.sealNumber = str('invalidSealNumber');
    }

    if (!newErrors.deviceSerial && deviceSerial && (deviceSerial === samModule || deviceSerial === fiscalNumber)) {
      newErrors.deviceSerial = str('mustDifferSamSerial');
    }
    const sealUpper = sealNumber.toUpperCase();
    if (!newErrors.sealNumber && sealNumber && [samModule, fiscalNumber, deviceSerial].some(v => v && v.toUpperCase() === sealUpper)) {
      newErrors.sealNumber = str('mustDifferSeal');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCheckSerial = () => {
    if (!form.taxNumber) return;
    setMessage(null);
    checkSerialMutation.mutate(form.taxNumber, {
      onSuccess: (response: any) => {
        if (response?.data?.orgName) {
          updateForm({ orgName: response.data.orgName });
        }
      },
      onError: () => {
        setMessage({ type: 'error', text: str('notFound') });
      }
    });
  };

  // Empty dropdowns are '' in the form; the server expects null for a missing reference
  const buildPayload = () => {
    const refs = ['region', 'district', 'street', 'activityType', 'objectType', 'deviceModel'] as const;
    const data: Record<string, unknown> = { ...form, id: document?.id || null };
    refs.forEach(key => {
      if (data[key] === '') data[key] = null;
    });
    return data;
  };

  const handleSubmit = () => {
    if (!validate(true)) return;
    setMessage(null);

    const data = buildPayload();

    sendMutation.mutate(data as any, {
      onSuccess: (res: any) => {
        setMessage({ type: 'success', text: `${str('sendDocumentSuccess')} ${res.data.id}` });
        onSuccess();
        setTimeout(onHide, 2000);
      },
      onError: () => {
        setMessage({ type: 'error', text: str('errorSendingDocument') });
      }
    });
  };

  const handleSaveDraft = () => {
    if (!validate(false)) return;
    setMessage(null);

    const data = buildPayload();

    saveDraftMutation.mutate(data as any, {
      onSuccess: () => {
        setMessage({ type: 'success', text: str('draftSaved') });
        onSuccess();
      },
      onError: () => {
        setMessage({ type: 'error', text: str('couldNotSaveDraft') });
      }
    });
  };

  const handleDeleteDraft = () => {
    if (!document?.id) return;
    deleteDraftMutation.mutate(document.id, {
      onSuccess: () => {
        onSuccess();
        onHide();
      }
    });
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
      {isDraft && (
        <Button label={str('delete')} icon="pi pi-trash" severity="danger" outlined onClick={handleDeleteDraft} loading={deleteDraftMutation.isLoading} />
      )}
      {(isDraft || isError || !isEdit) && (
        <>
          <Button label={str('save')} icon="pi pi-save" severity="secondary" onClick={handleSaveDraft} loading={saveDraftMutation.isLoading} />
          <Button label={str('submit')} icon="pi pi-send" onClick={handleSubmit} loading={sendMutation.isLoading} />
        </>
      )}
    </div>
  );

  const title = isEdit ? (isDraft ? str('editApplicationTitle') : str('newApplicationTitle')) : str('newApplicationTitle');

  return (
    <Dialog header={title} visible={visible} onHide={onHide} footer={footer} style={{ width: '95vw', maxWidth: '1100px' }} modal>
      {!isDraft && document?.statusText && (
        <Message severity={isError ? 'error' : 'info'} text={document.statusText} style={{ marginBottom: 15, width: '100%' }} />
      )}

      <div className="app-form-grid">
        {/* Row 1: Tax & Org */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('taxNumber')}</label>
          <div style={{ display: 'flex', gap: 5 }}>
            <InputText
              value={form.taxNumber}
              onChange={(e) => updateForm({ taxNumber: e.target.value })}
              className={errors.taxNumber ? 'p-invalid' : ''}
              style={{ flex: 1 }}
            />
            <Button icon="pi pi-check" onClick={handleCheckSerial} loading={checkSerialMutation.isLoading} />
          </div>
          {errors.taxNumber && <small className="p-error">{errors.taxNumber}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('orgName')}</label>
          <InputText value={form.orgName} disabled className={errors.orgName ? 'p-invalid' : ''} />
          {errors.orgName && <small className="p-error">{errors.orgName}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('phone')}</label>
          <InputText
            value={form.phone}
            onChange={(e) => updateForm({ phone: e.target.value })}
            className={errors.phone ? 'p-invalid' : ''}
          />
          {errors.phone && <small className="p-error">{errors.phone}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('email')}</label>
          <InputText value={form.email} onChange={(e) => updateForm({ email: e.target.value })} />
        </div>

        {/* Row 2: Address - Region, District, Street on same line */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('region')}</label>
          <SearchSelect
            value={form.region}
            options={regionQuery.data?.data}
            onChange={(value) => updateForm({ region: value, district: '', street: '' })}
            optionLabel="name"
            optionValue="id"
            className={errors.region ? 'p-invalid' : ''}
          />
          {errors.region && <small className="p-error">{errors.region}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('district')}</label>
          <SearchSelect
            value={form.district}
            options={districtQuery.data?.data}
            onChange={(value) => updateForm({ district: value, street: '' })}
            optionLabel="name"
            optionValue="id"
            disabled={!form.region}
            className={errors.district ? 'p-invalid' : ''}
          />
          {errors.district && <small className="p-error">{errors.district}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('street')}</label>
          <SearchSelect
            value={form.street}
            options={streetQuery.data?.data}
            onChange={(value) => updateForm({ street: value })}
            optionLabel="name"
            optionValue="id"
            disabled={!form.district}
            className={errors.street ? 'p-invalid' : ''}
          />
          {errors.street && <small className="p-error">{errors.street}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('addressAdditional')}</label>
          <InputText
            value={form.addressAdditional}
            onChange={(e) => updateForm({ addressAdditional: e.target.value })}
            className={errors.addressAdditional ? 'p-invalid' : ''}
          />
          {errors.addressAdditional && <small className="p-error">{errors.addressAdditional}</small>}
        </div>

        {/* Row 3: Activity & Object */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('activityType')}</label>
          <SearchSelect
            value={form.activityType}
            options={activityTypesQuery.data?.data}
            onChange={(value) => updateForm({ activityType: value })}
            optionLabel="name"
            optionValue="id"
            className={errors.activityType ? 'p-invalid' : ''}
          />
          {errors.activityType && <small className="p-error">{errors.activityType}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('objectType')}</label>
          <SearchSelect
            value={form.objectType}
            options={objectTypesQuery.data?.data}
            onChange={(value) => updateForm({ objectType: value })}
            optionLabel="name"
            optionValue="id"
            className={errors.objectType ? 'p-invalid' : ''}
          />
          {errors.objectType && <small className="p-error">{errors.objectType}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('samModule')}</label>
          <InputText
            value={form.samModule}
            onChange={(e) => updateForm({ samModule: e.target.value.replace(/\D/g, '').slice(0, 8) })}
            inputMode="numeric"
            className={errors.samModule ? 'p-invalid' : ''}
          />
          {errors.samModule && <small className="p-error">{errors.samModule}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('deviceSerial')}</label>
          <InputText
            value={form.deviceSerial}
            onChange={(e) => updateForm({ deviceSerial: e.target.value.replace(/\D/g, '').slice(0, 8) })}
            inputMode="numeric"
            className={errors.deviceSerial ? 'p-invalid' : ''}
          />
          {errors.deviceSerial && <small className="p-error">{errors.deviceSerial}</small>}
        </div>

        {/* Row 4 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('sealNumber')}</label>
          <InputText
            value={form.sealNumber}
            onChange={(e) => updateForm({ sealNumber: e.target.value.replace(/\s/g, '') })}
            maxLength={255}
            className={errors.sealNumber ? 'p-invalid' : ''}
          />
          {errors.sealNumber && <small className="p-error">{errors.sealNumber}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('simNumber')}</label>
          <InputText value={form.simNumber} onChange={(e) => updateForm({ simNumber: e.target.value })} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('fiscalNumber')}</label>
          <InputText
            value={form.fiscalNumber}
            onChange={(e) => updateForm({ fiscalNumber: e.target.value.replace(/\D/g, '').slice(0, 8) })}
            inputMode="numeric"
            className={errors.fiscalNumber ? 'p-invalid' : ''}
          />
          {errors.fiscalNumber && <small className="p-error">{errors.fiscalNumber}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('deviceModel')}</label>
          <SearchSelect
            value={form.deviceModel}
            options={deviceModelsQuery.data?.data}
            onChange={(value) => updateForm({ deviceModel: value })}
            optionLabel="name"
            optionValue="id"
            className={errors.deviceModel ? 'p-invalid' : ''}
          />
          {errors.deviceModel && <small className="p-error">{errors.deviceModel}</small>}
        </div>
      </div>

      {message && (
        <Message severity={message.type} text={message.text} style={{ marginTop: 15, width: '100%' }} />
      )}
    </Dialog>
  );
}
